/* eslint-disable react/jsx-no-bind */
import { Box } from '@ouster/webviz-react-components';

import { Tooltip } from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import {
  MdOutlineFolderShared,
  MdUpload,
  MdOutlineCollectionsBookmark,
  MdOutlinePublic,
  MdOutlineStarPurple500,
} from 'react-icons/md';
import {
  RiRecordCircleLine,
  RiBuildingLine,
  RiArrowLeftDoubleFill,
  RiArrowRightDoubleFill,
  RiFileAddLine,
} from 'react-icons/ri';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import useWindowResize from '../hooks/useWindowResize';
import { useStoreActions, useStoreState } from '../reducers';

const StyledNavContainer = styled.nav`
  display: grid;
`;

const StyledToggle = styled.div<{ collapsed: boolean }>`
  height: 2rem;
  display: none;
  justify-content: center;
  padding: 0;
  align-items: center;
  font-size: 20px;
  cursor: pointer;
  margin: -1rem 0 0;

  border-bottom: 1px solid var(--ouster-colors-gray-700);

  @media (max-width: 1400px) {
    display: flex;
    justify-content: ${(props) => (props.collapsed ? 'center' : 'flex-end')};
    padding-right: ${(props) => (props.collapsed ? 0 : '1rem')};
    background-color: #5e79fb24;
  }
`;

const StyledButton = styled.button<{ collapsed: boolean; variant?: string }>`
  text-align: left;
  display: flex;
  font-size: 14px;
  margin-inline: 1rem;
  column-gap: 0.5rem;
  border-radius: 4px;
  align-items: center;
  padding: 0.5rem 0.75rem;
  font-size: 500;
  white-space: nowrap;
  cursor: pointer;
  line-height: 1;
  background-color: ${(props) =>
    props.variant === 'solid' ? 'var(--color-blast-off-blue)' : 'transparent'};
  border: 1px solid;
  border-color: ${(props) =>
    props.variant === 'solid' ? 'transparent' : 'var(--color-blast-off-blue)'};
  color: ${(props) =>
    props.variant === 'solid' ? 'white' : 'var(--color-blast-off-blue)'};
  svg {
    font-size: 24px;
  }

  &:hover {
    filter: brightness(130%);
  }

  @media (max-width: 1400px) {
    span {
      display: ${(props) => (props.collapsed ? 'none' : 'flex')};
    }
  }
`;

const StyledNavLink = styled.a<{ isActive: boolean; collapsed: boolean }>`
  text-align: left;
  display: flex;
  font-size: 14px;
  column-gap: 0.5rem;
  align-items: center;
  padding: 0.85rem 1.75rem 0.85rem 1.75rem;
  border-bottom: 1px solid var(--ouster-colors-whiteAlpha-200);
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  line-height: 1;
  background-color: ${(props) =>
    props.isActive ? 'var(--ouster-colors-blue-700)' : 'transparent'};
  color: ${(props) => (props.isActive ? 'white' : 'var(--color-gray-4)')};
  svg {
    font-size: 24px;
  }

  &:hover {
    background-color: ${(props) =>
      props.isActive
        ? 'var(--ouster-colors-blue-700)'
        : 'var(--ouster-colors-whiteAlpha-200)'};
    color: ${(props) =>
      props.isActive ? 'white' : 'var(--ouster-colors-blue-400)'};
    cursor: ${(props) => (props.isActive ? 'default' : 'pointer')};
  }

  @media (max-width: 1400px) {
    span {
      display: ${(props) => (props.collapsed ? 'none' : 'flex')};
    }
  }
`;

const StyledSubNavLink = styled(StyledNavLink)`
  padding-left: 2.75rem;
  padding-block: 0.75rem;
  font-weight: 400;
  @media (max-width: 1400px) {
    display: ${(props) => (props.collapsed ? 'none' : 'flex')};
  }
`;

const StyledIndicator = styled.div`
  width: 8px;
  height: 8px;
  background-color: #22c55e; /* Green color */
  border-radius: 50%;
  margin-left: auto;
  flex-shrink: 0;
`;

interface Tab {
  name: string;
  path: string;
  icon: JSX.Element;
  indicator?: boolean;
  submenus?: {
    icon: JSX.Element;
    name: string;
    path: string;
    indicator?: boolean;
  }[];
}

const OUSTER_ONLY_TABS = ['Architect Scenes'];
const SUPPORT_ONLY_TABS = ['Shared with Ouster'];

type SideNavMode = 'private' | 'public';
interface SideNavProps {
  mode?: SideNavMode;
}

/**
 *
 */
export default function SideNav({ mode = 'private' }: SideNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { curUserOrg, isOusterUser } = useStoreState((storeState) => ({
    ...storeState.user,
  }));
  const { newContentStatus } = useStoreState((storeState) => ({
    ...storeState.drive,
  }));

  const { setIsOpen: setUploadVisibility, setIsPublicWarningOpen } =
    useStoreActions((actions) => actions.recordingUpload);

  const { width } = useWindowResize();
  const [collapsed, setCollapsed] = useState(width <= 1400);

  const tabs: Tab[] = useMemo(
    () =>
      mode === 'public'
        ? [
            {
              name: 'Recordings',
              path: '/public/recordings',
              icon: <RiRecordCircleLine />,
            },
            {
              name: 'Shared with Ouster',
              path: '/public/shared_with_ouster',
              icon: <MdOutlineFolderShared />,
            },
            {
              name: 'Sample Library',
              path: '/public/sample_library',
              icon: <MdOutlineCollectionsBookmark />,
              submenus: [
                {
                  name: 'Public Collection',
                  path: '/public/sample_library/public_collection',
                  icon: <MdOutlinePublic />,
                },
                {
                  name: 'Partner Previews',
                  path: '/public/sample_library/partner_preview',
                  icon: <MdOutlineStarPurple500 />,
                },
              ],
            },
          ]
        : [
            {
              name: 'Recordings',
              path: '/drive/private',
              icon: <RiRecordCircleLine />,
            },
            {
              name: 'Shared with Ouster',
              path: '/shared_with_ouster',
              icon: <MdOutlineFolderShared />,
            },
            {
              name: 'Architect Scenes',
              path: '/architect_scene',
              icon: <RiBuildingLine />,
            },
            {
              name: 'Sample Library',
              path: '/drive/sample_library',
              icon: <MdOutlineCollectionsBookmark />,
              submenus: [
                {
                  name: 'Public Collection',
                  path: '/drive/sample_library/public_collection',
                  icon: <MdOutlinePublic />,
                  indicator: newContentStatus?.PUBLIC_PRIVATE,
                },
                {
                  name: 'Partner Previews',
                  path: '/drive/sample_library/partner_preview',
                  icon: <MdOutlineStarPurple500 />,
                  indicator: newContentStatus?.SPECIFIC_ORGS,
                },
              ],
            },
          ],
    [mode, newContentStatus?.PUBLIC_PRIVATE, newContentStatus?.SPECIFIC_ORGS]
  );

  useEffect(() => {
    setCollapsed(width <= 1400);
  }, [width]);

  function handleToggle() {
    setCollapsed(!collapsed);
  }

  function handleUploadClick() {
    if (mode === 'public') {
      setIsPublicWarningOpen(true);
    } else {
      setUploadVisibility(true);
    }
  }

  function handleNavClick(path: string, hasSubmenus: boolean) {
    if (hasSubmenus && collapsed) {
      setCollapsed(false);
    }
    navigate(path);
  }

  function isPathActive(path: string, submenus?: any[]): boolean {
    const isSubmenuActive =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      submenus?.some((submenu) => location.pathname === submenu.path) ?? false;

    if (collapsed) {
      // If collapsed, consider the menu active when it matches path or has active submenu
      return location.pathname === path || isSubmenuActive;
    }

    // If expanded, consider menu active only when the path match
    return location.pathname === path;
  }

  function loadNewScene() {
    const url = `${window.location.origin}/architect/?new`;
    window.open(url, '_blank');
  }

  return (
    <StyledNavContainer data-testid="side-nav-root">
      <StyledToggle onClick={handleToggle} collapsed={collapsed} data-testid="side-nav-toggle">
        {collapsed ? <RiArrowRightDoubleFill /> : <RiArrowLeftDoubleFill />}
      </StyledToggle>

      {/* FUNCTION BUTTONS */}
      <Box
        display="grid"
        rowGap="1rem"
        mt="1rem"
        pb="1.5rem"
        borderBottom="1px solid"
        borderColor="var(--color-unify-white-30)"
        data-testid="side-nav-function-buttons"
      >
        <Tooltip
          title={
            collapsed ? (
              <div style={{ fontSize: 14, padding: 4 }}>Upload Data</div>
            ) : null
          }
          arrow
          placement="right"
          sx={{ fontSize: 34 }}
        >
          <StyledButton
            collapsed={collapsed}
            variant="solid"
            onClick={handleUploadClick}
            data-testid="side-nav-upload-btn"
          >
            <MdUpload />
            <span>Upload Data</span>
          </StyledButton>
        </Tooltip>
        {/* 
          TODO: vivian - currently we just simply hide "new architect" button
          for all non-ouster org. we need to a better feature flag to check in the future
        */}
        {curUserOrg?.id === 1 && (
          <Tooltip
            title={
              collapsed ? (
                <div style={{ fontSize: 14, padding: 4 }}>New Architect</div>
              ) : null
            }
            arrow
            placement="right"
            sx={{ fontSize: 34 }}
          >
            <StyledButton
              collapsed={collapsed}
              variant="outline"
              onClick={loadNewScene}
              data-testid="side-nav-new-architect-btn"
            >
              <RiFileAddLine />
              <span>New Architect</span>
            </StyledButton>
          </Tooltip>
        )}
      </Box>

      {/* NAV LINKS */}
      {tabs.map((tab) => {
        /* 
          Hiding architect scenes for non-ouster users in the first phase of multi tenancy
        */
        if (OUSTER_ONLY_TABS.includes(tab.name) && !isOusterUser) {
          return null;
        }
        if (
          isOusterUser &&
          SUPPORT_ONLY_TABS.includes(tab.name) &&
          curUserOrg?.role === 'BASIC'
        ) {
          return null;
        }
        return (
          <div key={tab.name} data-testid={`side-nav-tab-${tab.name.replace(/\s+/g, '-').toLowerCase()}`}> 
            <Tooltip
              title={
                collapsed ? (
                  <div style={{ fontSize: 14, padding: 4 }}>{tab.name}</div>
                ) : null
              }
              arrow
              placement="right"
              sx={{ fontSize: 34 }}
            >
              <StyledNavLink
                isActive={isPathActive(tab.path, tab.submenus)}
                onClick={() => handleNavClick(tab.path, !!tab.submenus)}
                collapsed={collapsed}
                data-testid={`side-nav-link-${tab.name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {tab.icon}
                <span>{tab.name}</span>
                {tab.indicator && <StyledIndicator />}
              </StyledNavLink>
            </Tooltip>
            {/* Render submenus if they exist */}
            {tab.submenus && (
              <div data-testid={`side-nav-submenus-${tab.name.replace(/\s+/g, '-').toLowerCase()}`}>
                {tab.submenus.map((submenu) => (
                  <StyledSubNavLink
                    key={submenu.name}
                    isActive={isPathActive(submenu.path)}
                    onClick={() => handleNavClick(submenu.path, false)}
                    collapsed={collapsed}
                    data-testid={`side-nav-sublink-${submenu.name.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {submenu.icon}
                    <span>{submenu.name}</span>
                    {submenu.indicator && <StyledIndicator />}
                  </StyledSubNavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </StyledNavContainer>
  );
}
